import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { AppIcon } from "@/components/AppIcon";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Wallet } from "lucide-react";

function FoglioCassaManagersPanel({ users }) {
  const [ids, setIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/fc/managers")
      .then(({ data }) => setIds(data.user_ids || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (uid, enabled) => {
    setSaving(uid);
    try {
      const { data } = await api.post("/fc/managers", { user_id: uid, enabled });
      setIds(data.user_ids || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const eligible = users.filter((u) => u.role !== "admin");

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 shadow-sm" data-testid="fc-managers-panel">
      <div className="flex items-center gap-2 mb-1">
        <Wallet className="h-5 w-5 text-emerald-600" />
        <h3 className="font-heading text-lg text-slate-900">Responsabili amministrativi — Foglio Cassa</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        I responsabili selezionati vedono i Fogli Cassa di <b>tutti</b> i produttori (cruscotto di sola visione).
        Gli amministratori hanno sempre accesso completo. I produttori vedono solo il proprio foglio.
      </p>
      {loading ? (
        <p className="text-sm text-slate-400">Caricamento…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {eligible.map((u) => {
            const has = ids.includes(u.user_id);
            return (
              <label
                key={u.user_id}
                data-testid={`fc-manager-${u.user_id}`}
                className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-all ${has ? "border-emerald-400 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <Checkbox
                  checked={has}
                  disabled={saving === u.user_id}
                  onCheckedChange={(c) => toggle(u.user_id, c)}
                  className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                />
                <div className="min-w-0">
                  <div className="text-sm text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-400 truncate">{u.email}</div>
                </div>
              </label>
            );
          })}
          {eligible.length === 0 && <p className="text-sm text-slate-400">Nessun utente non-admin configurato.</p>}
        </div>
      )}
    </div>
  );
}

export default function PermissionsTab({ users, apps, reload }) {
  const [saving, setSaving] = useState(null);

  const togglePermission = async (user, appId, checked) => {
    const current = new Set(user.permissions || []);
    if (checked) current.add(appId);
    else current.delete(appId);
    setSaving(user.user_id);
    try {
      await api.put(`/users/${user.user_id}`, { permissions: Array.from(current) });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      <FoglioCassaManagersPanel users={users} />
      <div>
        <h3 className="font-heading text-lg text-slate-900 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-brand-blue" /> Assegnazione permessi
        </h3>
        <p className="text-sm text-slate-500">Scegli quali app può vedere ciascun utente. Gli amministratori vedono tutte le app.</p>
      </div>

      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.user_id} data-testid={`perm-user-${u.user_id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{u.name}</span>
                  {u.role === "admin" && <Badge className="bg-brand-blue/15 text-brand-blue border-0 hover:bg-brand-blue/15">Admin</Badge>}
                </div>
                <span className="text-xs text-slate-500">{u.email}</span>
              </div>
              {saving === u.user_id && <span className="text-xs text-slate-400">Salvataggio…</span>}
            </div>

            {u.role === "admin" ? (
              <p className="text-sm text-slate-400 italic">Accesso completo a tutte le applicazioni.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {apps.map((a) => {
                  const has = (u.permissions || []).includes(a.app_id);
                  return (
                    <label
                      key={a.app_id}
                      data-testid={`perm-${u.user_id}-${a.app_id}`}
                      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-all ${has ? "border-brand-blue/40 bg-brand-blue/5" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <Checkbox
                        checked={has}
                        onCheckedChange={(c) => togglePermission(u, a.app_id, c)}
                        className="data-[state=checked]:bg-brand-blue data-[state=checked]:border-brand-blue"
                      />
                      <span style={{ color: a.color }}><AppIcon name={a.icon} className="h-4 w-4" /></span>
                      <span className="text-sm text-slate-900 truncate">{a.name}</span>
                    </label>
                  );
                })}
                {apps.length === 0 && <p className="text-sm text-slate-400">Nessuna app configurata.</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
